import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  EnvelopeIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, Spinner, Modal, SearchInput } from '../../components/ui';
import { sysadminService } from '../../services/sysadmin.service';
import { toast } from 'react-toastify';

interface EmailTemplate {
  id: string;
  templateName: string;
  subject: string;
  body: string;
  replyTo?: string;
  companyId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const EmailTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Modals
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);

  // Form state
  const [form, setForm] = useState({
    templateName: '',
    subject: '',
    body: '',
    replyTo: '',
  });

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await sysadminService.getEmailTemplates();
      setTemplates(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error('Failed to load email templates');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const filteredTemplates = useMemo(
    () =>
      templates.filter(
        (t) =>
          t.templateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.subject?.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [templates, searchTerm]
  );

  const handleCreate = () => {
    setSelectedTemplate(null);
    setForm({ templateName: '', subject: '', body: '', replyTo: '' });
    setIsEditorOpen(true);
  };

  const handleEdit = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setForm({
      templateName: template.templateName,
      subject: template.subject,
      body: template.body,
      replyTo: template.replyTo || '',
    });
    setIsEditorOpen(true);
  };

  const handlePreview = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setIsPreviewOpen(true);
  };

  const handleSave = async () => {
    try {
      if (selectedTemplate) {
        await sysadminService.updateEmailTemplate(selectedTemplate.id, form);
        toast.success('Template updated');
      } else {
        await sysadminService.createEmailTemplate(form);
        toast.success('Template created');
      }
      setIsEditorOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save template');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await sysadminService.deleteEmailTemplate(id);
      toast.success('Template deleted');
      fetchTemplates();
    } catch {
      toast.error('Failed to delete template');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-semibold text-secondary-900 dark:text-secondary-50">
            Email Templates
          </h1>
          <p className="text-secondary-500 dark:text-secondary-400">
            Manage default email templates used across all companies
          </p>
        </div>
        <Button onClick={handleCreate}>
          <PlusIcon className="w-4 h-4 mr-1" />
          Create Template
        </Button>
      </div>

      {/* Search */}
      <Card padding="sm">
        <div className="p-4">
          <SearchInput
            placeholder="Search templates by name or subject..."
            value={searchTerm}
            onChange={setSearchTerm}
          />
          {searchTerm && (
            <p className="mt-2 text-sm text-secondary-500">
              Showing {filteredTemplates.length} of {templates.length} templates
            </p>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary-50 dark:bg-secondary-800 border-b border-secondary-200 dark:border-secondary-700">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Template Name</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Subject</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Reply To</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Last Updated</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
              {filteredTemplates.map((template) => (
                <tr key={template.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-800/50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-secondary-900 dark:text-secondary-50">{template.templateName}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-secondary-600 dark:text-secondary-400 max-w-xs truncate">{template.subject}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-secondary-500 dark:text-secondary-400">
                    {template.replyTo || 'Not set'}
                  </td>
                  <td className="px-6 py-4 text-sm text-secondary-500 dark:text-secondary-400">
                    {new Date(template.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={() => handlePreview(template)}>
                        <EyeIcon className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => handleEdit(template)}>
                        <PencilIcon className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleDelete(template.id)}>
                        <TrashIcon className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <EnvelopeIcon className="mx-auto h-12 w-12 text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-secondary-50">
              {templates.length === 0 ? 'No email templates found' : 'No templates match your search'}
            </h3>
            <p className="mt-1 text-sm text-secondary-500">
              {templates.length === 0 ? 'Get started by creating a new template.' : 'Try adjusting your search criteria.'}
            </p>
          </div>
        )}
      </Card>

      {/* Editor Modal */}
      <Modal
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        title={selectedTemplate ? 'Edit Email Template' : 'Create Email Template'}
        size="xl"
      >
        <div className="space-y-4 p-1">
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Template Name</label>
            <input
              type="text"
              value={form.templateName}
              onChange={(e) => setForm({ ...form, templateName: e.target.value })}
              placeholder="e.g. ORDER_RECEIVED"
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Subject</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Reply To</label>
            <input
              type="email"
              value={form.replyTo}
              onChange={(e) => setForm({ ...form, replyTo: e.target.value })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Body (HTML)</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={12}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <Button variant="secondary" onClick={() => setIsEditorOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>{selectedTemplate ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        title="Email Preview"
        size="xl"
      >
        {selectedTemplate && (
          <div className="p-4 space-y-4">
            <div>
              <span className="text-xs font-medium text-secondary-500 uppercase">Subject</span>
              <p className="text-sm text-secondary-900 dark:text-secondary-50">{selectedTemplate.subject}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-secondary-500 uppercase">Reply To</span>
              <p className="text-sm text-secondary-900 dark:text-secondary-50">{selectedTemplate.replyTo || 'Not set'}</p>
            </div>
            <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
              <iframe
                srcDoc={selectedTemplate.body}
                title="Email Preview"
                className="w-full h-96 bg-white"
                sandbox=""
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EmailTemplates;
